import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router";
import { PrivateRoute } from "@/auth/PrivateRoute";
import { useFeatureFlags, useFeatureFlagsLoading } from "@/lib/feature-flags";
import { VoiceLayout } from "./VoiceLayout";

const RegisterIndexPage = lazy(() => import("./register/RegisterIndexPage"));
const RegisterSetupPage = lazy(() => import("./register/RegisterSetupPage"));
const RegisterPendingRebindPage = lazy(
  () => import("./register/RegisterPendingRebindPage"),
);
const AccountIndexPage = lazy(() => import("./account/AccountIndexPage"));
const AccountMcpPage = lazy(() => import("./account/AccountMcpPage"));
const AccountSmsPreferencesPage = lazy(
  () => import("./account/AccountSmsPreferencesPage"),
);
const AgentResultPage = lazy(() => import("../agent-result/AgentResultPage"));

/**
 * Caller-facing voice-auth flow. Lives at /voice/* so the SMS magic
 * link is short. Two access modes are mixed:
 *
 * - Public (token in URL): magic-link landing + SMS preferences toggle.
 * - Session-authed (post-Dex): PIN setup, account management, MCP list.
 */
export function VoiceRouter() {
  const flags = useFeatureFlags();
  const flagsLoading = useFeatureFlagsLoading();
  // useFeatureFlags falls back to all-false DEFAULTS while the query is
  // in flight — deciding on those would bounce every cold deep-link
  // (e.g. the SMS magic link) to the dashboard before flags arrive.
  if (flagsLoading) {
    return null;
  }
  if (!flags.voiceAuth) {
    return <Navigate to="/monitoring/dashboard" replace />;
  }
  return (
    <VoiceLayout>
      <Suspense fallback={null}>
        <Routes>
          <Route index element={<Navigate to="register" replace />} />
          <Route path="register" element={<RegisterIndexPage />} />
          <Route
            path="account/sms-preferences"
            element={<AccountSmsPreferencesPage />}
          />
          <Route path="agent-result" element={<AgentResultPage />} />

          <Route element={<PrivateRoute />}>
            <Route path="register/setup" element={<RegisterSetupPage />} />
            <Route
              path="register/pending-rebind"
              element={<RegisterPendingRebindPage />}
            />
            <Route path="account" element={<AccountIndexPage />} />
            <Route path="account/mcp" element={<AccountMcpPage />} />
          </Route>

          <Route path="*" element={<Navigate to="register" replace />} />
        </Routes>
      </Suspense>
    </VoiceLayout>
  );
}
